import React from 'react';
import {
    Modal,
    ModalHeader,
    ModalFooter,
    Banner,
    MandatoryIndicator,
    MarkdownEditor,
    Select,
    Tooltip,
} from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidationForm, ValidatingInput, ValidationSummary } from '@/components/forms/validation';
import { useGetActiveProjectId, useMergeState } from '@/utils/hooks';
import { BuyerOptionPresetGroup } from '@/types';
import { useRooms } from '@/services/rooms';
import {
    OptionPresetGroupType,
    OptionPresetGroupUpdateOptions,
    useOptionPresetGroupCreate,
    useOptionPresetGroupUpdate,
} from '@/services/option-preset/option-preset-group';
import { Button, ButtonBar, Checkbox } from '@ygtechnologies/rubus-ui';
import { getRoomDisplayName } from '@/utils/rooms';
import { ProjectDeadlineSelect } from '../../project/ProjectSettings/ProjectDeadlineSelect';

interface BuyerOptionPresetGroupEditDialogProps {
    group?: BuyerOptionPresetGroup;
    show: boolean;
    onClose(): void;
    onSave(): void;
    presetType: OptionPresetGroupType;
}

function getEditState(
    item?: BuyerOptionPresetGroup,
    defaultPresetType?: OptionPresetGroupType
): OptionPresetGroupUpdateOptions {
    if (!item) {
        return {
            name: '',
            description: '',
            forceSelection: false,
            styleOptionsFree: false,
            legacyMode: false,
            type: defaultPresetType || OptionPresetGroupType.Room,
            projectId: 0,
        };
    }

    return {
        name: item.name,
        description: item.description,
        forceSelection: item.forceSelection,
        styleOptionsFree: item.styleOptionsFree,
        legacyMode: item.legacyMode,
        type: item.type || defaultPresetType || OptionPresetGroupType.Room,
        projectId: item.projectId,
        roomIds: item.tags.map((x) => x.id),
    };
}

const BuyerOptionPresetGroupEditDialog: React.FC<BuyerOptionPresetGroupEditDialogProps> = ({
    group,
    show,
    onClose,
    onSave,
    presetType,
}) => {
    const [editState, mergeEditState, setEditState] = useMergeState<OptionPresetGroupUpdateOptions>(
        getEditState(group, presetType)
    );
    const projectId = useGetActiveProjectId();
    const roomQuery = useRooms(projectId!);
    // const [rooms] = useAsyncStateWithIsLoading(() => getRooms(projectId!), [projectId], null);
    const updateMutation = useOptionPresetGroupUpdate();
    const createMutation = useOptionPresetGroupCreate();

    const reset = () => {
        setEditState(getEditState(group, presetType));
    };

    const handleSubmit = async () => {
        if (!projectId) {
            return;
        }

        try {
            if (group) {
                await updateMutation.mutateAsync([
                    group.id,
                    {
                        ...editState,
                        projectId: group.projectId,
                        type: editState.type,
                    },
                ]);
            } else {
                await createMutation.mutateAsync({
                    ...editState,
                    projectId: projectId,
                    type: editState.type,
                });
            }

            onSave();
            onClose();
        } catch (e: any) {
            throw e;
        }
    };
    const isSaving = updateMutation.isPending || createMutation.isPending;
    return (
        <Modal size="lg" isOpen={show} onOpen={reset}>
            <ModalHeader>
                {group ? (
                    editState.type === OptionPresetGroupType.Room ? (
                        <Trans>Edit room style group</Trans>
                    ) : (
                        <Trans>Edit main style group</Trans>
                    )
                ) : editState.type === OptionPresetGroupType.Room ? (
                    <Trans>Create a new room style group</Trans>
                ) : (
                    <Trans>Create a new main style group</Trans>
                )}
            </ModalHeader>
            <Banner type="info">
                <strong>
                    {editState.type === OptionPresetGroupType.Room ? (
                        <Trans>This group applies to rooms.</Trans>
                    ) : (
                        <Trans>This group applies to main styles.</Trans>
                    )}
                </strong>
                <Trans>
                    Style groups let you organize style options for buyers. You can require buyers to pick a style, make
                    options free by default, or allow picking outside style items. Use the description to explain the group’s
                    purpose.
                </Trans>
            </Banner>
            <ValidationForm className="form columns-1" onSubmit={handleSubmit}>
                <label>
                    <Trans>Style type</Trans>
                    <Select
                        value={{
                            value: editState.type,
                            label: editState.type === OptionPresetGroupType.Room ? t`Room` : t`Main`,
                        }}
                        options={[
                            { value: OptionPresetGroupType.Room, label: t`Room` },
                            { value: OptionPresetGroupType.Main, label: t`Main` },
                        ]}
                        onChange={(option: any) => mergeEditState({ type: option?.value || OptionPresetGroupType.Room })}
                    />
                </label>
                <label>
                    <Trans>Name</Trans>

                    <ValidatingInput
                        fieldName={t`Name`}
                        required
                        className="form-control"
                        placeholder={t`Name`}
                        value={editState.name}
                        onChange={(e) => mergeEditState({ name: e.target.value })}
                    />
                </label>
                {/* <label>
                    <Tooltip content={t`This is the internal name used for the style group, not visible for buyers`}>
                        <span>
                            <Trans>Internal name</Trans>
                            <TooltipQuestionMark />
                        </span>
                    </Tooltip>

                    <ValidatingInput
                        fieldName={t`Internal name`}
                        required
                        className="form-control"
                        placeholder={t`Internal name`}
                        value={editState.name}
                        onChange={(e) => mergeEditState({ name: e.target.value })}
                    />
                </label> */}
                {editState.type === OptionPresetGroupType.Room && (
                    <>
                        <label>
                            <Trans>Rooms</Trans>
                            <Select
                                isMulti
                                onChange={(value: any) => mergeEditState({ roomIds: value.map((v: any) => v.value) })}
                                value={
                                    editState.roomIds?.map((id) => {
                                        const r = roomQuery.data?.find((x) => x.id === id);
                                        return {
                                            value: id,
                                            label: r ? getRoomDisplayName(r.name, r.labelNote) : '',
                                        };
                                    }) || []
                                }
                                options={
                                    roomQuery.data?.map((x) => ({
                                        value: x.id,
                                        label: getRoomDisplayName(x.name, x.labelNote),
                                    })) || []
                                }
                            />
                        </label>
                    </>
                )}
                <label>
                    <Trans>Deadline</Trans>
                    <MandatoryIndicator />

                    <ProjectDeadlineSelect
                        value={editState.projectDeadlineId || null}
                        onChange={(deadlineId) => mergeEditState({ projectDeadlineId: deadlineId })}
                        projectId={projectId!}
                        required
                    />
                </label>

                <label>
                    <Trans>Description</Trans>
                    <MarkdownEditor
                        className="form-control"
                        markdown={editState.description}
                        onChange={(value) => mergeEditState({ description: value })}
                        minHeight="300px"
                    />
                </label>
                <Checkbox
                    inputSize="md"
                    checked={editState.forceSelection}
                    onChange={(e) => mergeEditState({ forceSelection: e.target.checked })}>
                    <Trans>Force buyer to choose style</Trans>
                </Checkbox>

                <Checkbox
                    inputSize="md"
                    checked={editState.styleOptionsFree}
                    onChange={(e) => mergeEditState({ styleOptionsFree: e.target.checked })}>
                    <Trans>Default style option free</Trans>
                </Checkbox>

                <Checkbox
                    inputSize="md"
                    checked={editState.legacyMode}
                    onChange={(e) => mergeEditState({ legacyMode: e.target.checked })}>
                    <Trans>Allow picking options outside style items</Trans>
                </Checkbox>
                <ValidationSummary />
                <ModalFooter>
                    <ButtonBar>
                        <ButtonBar.Right>
                            <Button disabled={isSaving} variant="tertiary" onClick={onClose}>
                                <Trans>Cancel</Trans>
                            </Button>
                            <Button type="submit" loading={isSaving} variant="primary">
                                <Trans>Save</Trans>
                            </Button>
                        </ButtonBar.Right>
                    </ButtonBar>
                </ModalFooter>
            </ValidationForm>
        </Modal>
    );
};

export default BuyerOptionPresetGroupEditDialog;
